import React from 'react';
import { Hammer, Loader2 } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { RoomSelector } from './RoomSelector';
import type { RoomEntry } from '../../types';

// ================================================================= //
//          RoomInputPanel — Room Description Input                    //
// ================================================================= //
// Combines room type selector, free-form text area for damage        //
// description, optional measurements (L x W x H), and a generate    //
// button. This is the primary input component for a single room.     //
// ================================================================= //

interface RoomInputPanelProps {
  /** The current room entry state. */
  roomEntry: RoomEntry;
  /** Called when any field in the room entry changes. */
  onUpdate: (entry: RoomEntry) => void;
  /** Called when the user clicks Generate Scope for this room. */
  onGenerate: (entry: RoomEntry) => void;
  /** Whether scope generation is in progress. */
  isGenerating: boolean;
}

export const RoomInputPanel: React.FC<RoomInputPanelProps> = ({
  roomEntry,
  onUpdate,
  onGenerate,
  isGenerating,
}) => {
  const handleRoomTypeChange = (value: string, label: string) => {
    onUpdate({
      ...roomEntry,
      room_type: value,
      room_type_label: label,
    });
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    onUpdate({ ...roomEntry, description: e.target.value });
  };

  const handleMeasurementChange = (
    field: 'length' | 'width' | 'height',
    value: string
  ) => {
    const numVal = value ? parseFloat(value) : undefined;
    const measurements = {
      ...roomEntry.measurements,
      [field]: numVal,
    };

    // Reason: Remove measurements object if all values are undefined
    const hasAny =
      measurements.length !== undefined ||
      measurements.width !== undefined ||
      measurements.height !== undefined;

    onUpdate({
      ...roomEntry,
      measurements: hasAny ? measurements : undefined,
    });
  };

  const canGenerate = roomEntry.description.trim().length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Room Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Room type selector */}
        <div className="space-y-2">
          <Label>Room Type</Label>
          <RoomSelector
            value={roomEntry.room_type}
            onChange={handleRoomTypeChange}
          />
        </div>

        {/* Damage description text area */}
        <div className="space-y-2">
          <Label htmlFor={`desc-${roomEntry.id}`}>Damage Description</Label>
          <textarea
            id={`desc-${roomEntry.id}`}
            value={roomEntry.description}
            onChange={handleDescriptionChange}
            placeholder="Describe the damage in this room. Include materials, dimensions, and affected areas..."
            rows={5}
            className={cn(
              'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
              'min-h-[120px] resize-y',
              'placeholder:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
            disabled={isGenerating}
          />
          <p className="text-xs text-muted-foreground">
            {roomEntry.description.length} characters
          </p>
        </div>

        {/* Optional measurements row */}
        <div className="space-y-2">
          <Label>Measurements (Optional)</Label>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                step={0.5}
                placeholder="L"
                value={roomEntry.measurements?.length ?? ''}
                onChange={(e) =>
                  handleMeasurementChange('length', e.target.value)
                }
                className="h-9 w-20 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                disabled={isGenerating}
                aria-label="Length in feet"
              />
              <span className="text-xs text-muted-foreground">ft</span>
            </div>

            <span className="text-muted-foreground">x</span>

            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                step={0.5}
                placeholder="W"
                value={roomEntry.measurements?.width ?? ''}
                onChange={(e) =>
                  handleMeasurementChange('width', e.target.value)
                }
                className="h-9 w-20 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                disabled={isGenerating}
                aria-label="Width in feet"
              />
              <span className="text-xs text-muted-foreground">ft</span>
            </div>

            <span className="text-muted-foreground">x</span>

            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                step={0.5}
                placeholder="H"
                value={roomEntry.measurements?.height ?? ''}
                onChange={(e) =>
                  handleMeasurementChange('height', e.target.value)
                }
                className="h-9 w-20 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                disabled={isGenerating}
                aria-label="Height in feet"
              />
              <span className="text-xs text-muted-foreground">ft</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            System uses defaults if not provided.
          </p>
        </div>

        {/* Error display */}
        {roomEntry.error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {roomEntry.error}
          </div>
        )}

        {/* Generate button */}
        <Button
          onClick={() => onGenerate(roomEntry)}
          disabled={!canGenerate || isGenerating}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Loader2 className="-ml-1 mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Hammer className="-ml-1 mr-2 h-4 w-4" />
              Generate Scope
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default RoomInputPanel;
